<template>
  <v-navigation-drawer eager v-model="isOpen" absolute right width="460" class="layer-drawer rounded-xl" style="height: calc(100% - 120px); box-shadow: 10px -3px 10px rgba(0, 0, 0, 0.2); z-index: 10">
    <v-app-bar flat dense class="layer-drawer-header">
      <div>
        <h4 class="mb-0">{{ $t("layers") }}</h4>
        <p class="subtitle">{{ $t("toggle-layers-on-off-for-visually-rich-experience") }}</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')" small>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-app-bar>
    <v-list class="pt-0 mt-15">
      <template v-for="source in styles">
        <div :key="source.id">
          <v-list-item>
            <v-list-item-avatar tile v-if="source.thumbnail" style="width: 88px; height: 58px; box-shadow: 0px 0px 8px -4px rgba(0, 0, 0, 0.7)">
              <v-img :src="require(`@/assets/layer-thumbs/${source.thumbnail}`)"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title style="font-size: 14px">{{ source?.lang?.[language]?.title || source.title }}</v-list-item-title>
              <v-list-item-subtitle class="subtitle" style="white-space: normal; font-size: 11px">{{ source?.lang?.[language]?.description || source.description }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch v-model="source.visible" @change="toggleSource(source)" color="primary"></v-switch>
            </v-list-item-action>
          </v-list-item>
          <div v-if="source.legend && source.visible" style="width: 100%; font-size: 12px; padding: 0px 0px 16px 16px; align: right">
            <a @click="toggleLegend(source.id)">{{ showLegend[source.id] ? $t("hide-legend") : $t("show-legend") }}</a>
            <div v-if="showLegend[source.id]">
              <div v-for="section in source.legend" :key="section.title">
                <h4>{{ section.title }}</h4>
                <ul style="list-style-type: none; padding-left: 8px">
                  <li v-for="item in section.items" :key="item.name">
                    <span :style="{backgroundColor: item.color}" class="legend-color"></span>
                    {{ item.lang[language] }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import {Vue, Component, Prop, Watch} from "vue-property-decorator"
import {config} from "./../../libs/utils"

@Component
export default class SidePanel extends Vue {
  styles = []
  showLegend = {}
  language = localStorage.getItem("user-language") || "en"
  originalLayerOrder = []
  sourcePanelsJson

  @Prop({type: Boolean, default: false}) isOpen
  @Prop({required: true}) map
  @Prop({type: Array, default: () => []}) activatePanel

  addedStyleMetadata = []

  created() {
    this.loadStyles()
  }
  async loadStyles() {
    const activePrefixCode = config.VUE_APP_PLOT_NUMBER_PREFIX.toLowerCase()

    let sourcePanels = await this.loadSourcePanelsJson(activePrefixCode)
    let sourcePanelsFresh = [...sourcePanels]
    this.styles = sourcePanelsFresh
    sourcePanelsFresh.forEach((source) => {
      if (source.visible) {
        this.addStyleFromUrl(source)
      }
    })
  }
  async loadSourcePanelsJson(activePrefixCode) {
    if (this.sourcePanelsJson) {
      return this.sourcePanelsJson
    }
    try {
      const response = await import(`@/assets/styles/${activePrefixCode}/source-panels.json`);
      this.sourcePanelsJson = Object.values(response.default || response)
      return this.sourcePanelsJson
    } catch (error) {
      console.error("Error loading source panels JSON:", error)
      return []
    }
  }

  beforeDestroy() {
    this.addedStyleMetadata = []
  }

  toggleSource(source) {
    if (source.visible) {
      this.addStyleFromUrl(source)
      this.toggleLegend(source.id)
    } else {
      this.removeStyleFromUrl(source)
    }
  }

  activateSourceAutomatically(sourceId) {
    const source = this.styles.find((s) => s.id === sourceId)
    if (source && !source.visible) {
      source.visible = true
      this.addStyleFromUrl(source)
      this.toggleLegend(source.id)
    }
  }

  @Watch("activatePanel")
  onActivatePanelChange(newVal) {
    if (Array.isArray(newVal)) {
      newVal.forEach((sourceId) => {
        this.activateSourceAutomatically(sourceId)
      })
    }
  }

  toggleLegend(sourceId) {
    this.$set(this.showLegend, sourceId, !this.showLegend[sourceId])
  }

  async addStyleFromUrl(source) {
    console.log("addStyleFromUrl", source)
    let styleUrl = source.url
    if (!styleUrl) {
      if (source.source && source.layer && source.layer.id) {
        const styleUrl = "exising-source::" + source.source
        const metadataEntry = {styleId: source.id, sources: [], layers: []}

        //add layer when source is existing - preloaded
        if (!this.map.getLayer(source.layer.id)) {
          this.map.addLayer({...source.layer})
          metadataEntry.layers.push(source.layer.id)
        }
        this.map.setLayoutProperty(source.layer.id, "visibility", "visible")

        if (source.logic) {
          if (source.logic && source.logic.if_present) {
            source.logic.if_present.forEach((logicItem) => {
              if (logicItem.sourceId && logicItem.url) {
                //find in meta if this source is already added
                let metaIndex = this.addedStyleMetadata.find((meta) => meta.styleId == logicItem.sourceId)
                if (metaIndex) {
                  this.removeStyleFromUrl({id: logicItem.sourceId})
                  let newSourceItem = this.styles.find((source) => source.id == logicItem.sourceId)
                  newSourceItem = Object.assign({}, newSourceItem, {url: logicItem.url})
                  this.addStyleFromUrl(newSourceItem)
                  metadataEntry.undo_source_change = {id: logicItem.sourceId}
                }
              }
            })
          }
        }

        if (source.terrain) {
          console.log("Setting up TERRAIN", source.terrain)
          this.map.setTerrain(source.terrain)
          metadataEntry.terrain = source.terrain
        }

        this.addedStyleMetadata.push(metadataEntry)
        console.log("metadataEntry", metadataEntry)

        return
      }
      console.error("No style URL found for source:", source)
      return
    }
    try {
      console.log("Adding style from URL", styleUrl)

      if (source.logic) {
        if (source.logic && source.logic.if_present) {
          console.log("if_present", source.logic.if_present)
          source.logic.if_present.forEach((logicItem) => {
            if (logicItem.layer) {
              if (this.map.getLayer(logicItem.layer)) {
                if (logicItem.url) {
                  styleUrl = logicItem.url
                }
              }
            }
          })
        }
      }
      const response = await fetch(styleUrl)
      const styleJson = await response.json()

      const metadataEntry = {
        styleId: source.id,
        sources: [],
        layers: [],
        terrain: null,
      }

      Object.keys(styleJson.sources).forEach((sourceId) => {
        console.log("sourceId", sourceId)
        if (!this.map.getSource(sourceId)) {
          console.log("have sourceId", sourceId, styleJson.sources[sourceId])
          this.map.addSource(sourceId, {...styleJson.sources[sourceId]})
          metadataEntry.sources.push(sourceId)
          console.log("Added source", sourceId)
        } else {
          console.log("this map has sourceId", sourceId)
        }
      })

      styleJson.layers?.forEach((layer) => {
        if (!this.map.getLayer(layer.id)) {
          // Save the current layer order
          if (!this.originalLayerOrder.length) {
            this.originalLayerOrder = this.map.getStyle().layers.map((l) => l.id)
          }

          this.map.addLayer({...layer})
          metadataEntry.layers.push(layer.id)

          if (source.position) {
            const referenceLayerId = this.getReferenceLayerId(source.position)
            if (referenceLayerId && this.map.getLayer(referenceLayerId)) {
              this.map.moveLayer(layer.id, referenceLayerId)
            }
          }
        }
      })

      if (styleJson.terrain) {
        console.log("Setting up TERRAIN", styleJson.terrain)
        this.map.setTerrain(styleJson.terrain)
        metadataEntry.terrain = styleJson.terrain
      }
      console.log("metadataEntry", metadataEntry)
      this.addedStyleMetadata.push(metadataEntry)
    } catch (error) {
      console.log("error adding style", error)
    }
  }

  removeStyleFromUrl(source) {
    console.log("removeStyleFromUrl", source)
    let styleUrl = source.url
    if (!styleUrl) {
      if (source.source && source.layer && source.layer.id) {
        styleUrl = "exising-source::" + source.source
        console.log("styleUrl for exsting layer", styleUrl)
      }
    }

    const metadataIndex = this.addedStyleMetadata.findIndex((meta) => meta.styleId === source.id)

    if (metadataIndex === -1) {
      console.warn("Style URL not found for removal:", styleUrl)
      return
    }

    const metadataEntry = this.addedStyleMetadata[metadataIndex]

    console.log("metadataEntry", metadataEntry)

    metadataEntry.layers.forEach((layerId) => {
      console.log("REMOVING layerId", layerId)
      if (this.map.getLayer(layerId)) {
        console.log("Confirm removal of layer", layerId)
        this.map.removeLayer(layerId)
        //hide layer
        // this.map.setLayoutProperty(layerId, "visibility", "none")
      }
    })

    // Restore the original layer order
    if (this.originalLayerOrder.length) {
      const currentLayers = this.map.getStyle().layers.map((l) => l.id)
      this.originalLayerOrder.forEach((layerId) => {
        if (currentLayers.includes(layerId)) {
          //this.map.moveLayer(layerId)
        }
      })
    }

    if (metadataEntry.terrain) {
      console.log("Clear TERRAIN", metadataEntry.terrain)
      this.map.setTerrain(null)
    }

    metadataEntry.sources.forEach((sourceId) => {
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId)
        //hide source
        // this.map.setLayoutProperty(sourceId, "visibility", "none")
      }
    })

    this.addedStyleMetadata.splice(metadataIndex, 1)

    if (metadataEntry.undo_source_change) {
      //remove this style and add the original style
      this.removeStyleFromUrl({id: metadataEntry.undo_source_change.id})
      let newSourceItem = this.styles.find((source) => source.id == metadataEntry.undo_source_change.id)
      this.addStyleFromUrl(newSourceItem)
    }
  }

  getReferenceLayerId(position) {
    const layers = this.map.getStyle().layers
    if (!layers || layers.length === 0) return null

    // Example logic: Find the layer ID at the desired position
    if (position === "top") {
      return layers[layers.length - 1].id // Topmost layer
    } else if (position === "bottom") {
      return layers[0].id // Bottommost layer
    }
    // Add custom logic for other positions if needed
    // For example, if you want to find a layer with a specific ID:
    const targetLayerId = position
    const targetLayer = layers.find((layer) => layer.id === targetLayerId)
    if (targetLayer) {
      return targetLayer.id // Return the ID of the target layer
    }
    // If no specific layer is found, return null
    return null
  }
}
</script>

<style>
.layer-drawer {
  margin: 50px 10px !important;
}

.subtitle {
  font-size: 12px;
  display: block;
}

.legend-color {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 5px;
}

.layer-drawer-header {
  padding: 8px 16px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 11;
  position: fixed;
}
</style>
